import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { customerActions } from "../slices/customer";
import { CustomerMapper } from "../../mappers/CustomerMapper";
import { getErrorMessage } from "../../utils/errorUtils";
import { CustomerRequestDto } from "../../shared/dtos/customer/CustomerRequestDto";
import { CustomerResponseDto } from "../../shared/dtos/customer/CustomerResponseDto";
import { CustomerSummaryDto } from "../../shared/dtos/customer/CustomerSummaryDto";
import { UpdateCustomerPayload } from "../../shared/interfaces/customer/UpdateCustomerPayload";
import { PaginationQueryDto } from "../../shared/dtos/common/PaginationQueryDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const getCustomers =
  (params?: PaginationQueryDto) => async (dispatch: AppDispatch) => {
    dispatch(customerActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<CustomerSummaryDto>>
      >("/customers", {
        headers: { requiresAuth: true },
        params,
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(customerActions.setItems(data.data.content));
        dispatch(customerActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(customerActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getCustomerById =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(customerActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<CustomerResponseDto>
      >(`/customers/${id}`, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(customerActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(customerActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const createCustomer =
  (values: CustomerRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(customerActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<CustomerResponseDto>
      >("/customers", values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Customer created successfully!");
        const summary = CustomerMapper.mapCustomerToSummary(data.data);
        dispatch(customerActions.createSuccess(summary));
        dispatch(
          customerActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(customerActions.setErrorCreate(message));
    }
  };

export const updateCustomer =
  (payload: UpdateCustomerPayload) => async (dispatch: AppDispatch) => {
    dispatch(customerActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<CustomerResponseDto>
      >(`/customers/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Customer updated successfully!");
        const summary = CustomerMapper.mapCustomerToSummary(data.data);
        dispatch(customerActions.updateSuccess(summary));
        dispatch(
          customerActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(customerActions.setErrorUpdate(message));
    }
  };

export const deleteCustomer = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(customerActions.setLoadingDelete());
  try {
    const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
      `/customers/${id}`,
      {
        headers: { requiresAuth: true },
      },
    );

    if (data.status === ApiResponseStatus.SUCCESS) {
      toast.success("Customer deleted successfully!");
      dispatch(customerActions.deleteSuccess(id));
      dispatch(
        customerActions.setServerStatus({
          operation: "delete",
          status: data.statusCode || 200,
        }),
      );
    }
  } catch (error) {
    dispatch(customerActions.setErrorDelete(getErrorMessage(error)));
  }
};

export const resetCustomerServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(customerActions.setServerStatus(null));
};
